import { Helmet } from 'react-helmet-async';

export default function Redigeringsguide() {
  return (
    <div className="pb-20">
      <Helmet>
        <title>Redigeringsguide | Dahliahjørnet</title>
        <meta name="description" content="Trin-for-trin guide til redigering af hjemmesiden for Dahliahjørnet." />
      </Helmet>
      {/* Header Banner */}
      <div className="relative h-[35vh] min-h-[250px] bg-forest-950 flex items-center justify-center text-center px-6">
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/50 to-transparent" />
        <div className="relative z-10 max-w-4xl">
          <span className="text-white uppercase tracking-[0.25em] font-bold text-xs sm:text-sm mb-3 block">
            Administrations-vejledning
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
            Redigeringsguide til Dahliahjørnet
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 mt-16">
        <div className="bg-white p-8 sm:p-12 rounded-3xl shadow-md border border-cream-200 flex flex-col gap-10">
          <div className="border-b border-cream-200 pb-6">
            <h2 className="text-2xl font-serif font-bold text-forest-950 mb-2">Hej og velkommen!</h2>
            <p className="text-charcoal-700 leading-relaxed">
              Denne guide er udarbejdet specielt til dig, så du nemt og hurtigt kan vedligeholde og opdatere hjemmesiden for Dahliahjørnet. Alt indhold på hjemmesiden styres nu via vores brugervenlige, 100% dansksprogede CMS-dashboard.
            </p>
          </div>

          {/* Trin 1: Log ind */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-dahlia-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                1
              </div>
              <h3 className="text-xl font-serif font-bold text-forest-950">Log ind på CMS-dashboardet</h3>
            </div>
            <div className="pl-13 flex flex-col gap-2 text-charcoal-700 text-sm leading-relaxed">
              <p>For at redigere indholdet skal du åbne din browser og gå til dit CMS-dashboard:</p>
              <div className="bg-cream-50 p-3 rounded-xl border border-cream-200 font-mono text-xs text-forest-950 select-all w-fit">
                https://cms.dev.auxo.dk/
              </div>
              <p>Indtast dine login-oplysninger (brugernavn og adgangskode), som du har modtaget.</p>
            </div>
          </div>

          {/* Trin 2: Knolde til salg */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-dahlia-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                2
              </div>
              <h3 className="text-xl font-serif font-bold text-forest-950">Styr dahlia-varianter og knolde til salg</h3>
            </div>
            <div className="pl-13 flex flex-col gap-2 text-charcoal-700 text-sm leading-relaxed">
              <p>Alle jeres dahlia-sorter administreres ét centralt sted under samlingen <strong>"dahliaknolde"</strong>. Herfra vises de automatisk i både galleriet og på bestillingslisten:</p>
              <ol className="list-decimal pl-5 flex flex-col gap-2 mt-1">
                <li>Klik på samlingen <strong>"dahliaknolde"</strong> i venstre sidepanel i CMS (her ligger alle jeres ca. 175 varianter).</li>
                <li><strong>Rediger en dahlia:</strong> Klik på dahliaen. Her kan du rette prisen i feltet <code>pris</code>, udskifte billedet under <code>billede</code> eller vælge <code>bogstavgruppe</code> fra dropdown-listen. Klik på <strong>"Gem"</strong>.</li>
                <li><strong>Styr hvad der er til salg (varierer efter sæson):</strong> I feltet <code>til_salg</code> styrer du med et enkelt klik, om sorten kan bestilles i den aktuelle sæson:
                  <ul className="list-disc pl-5 mt-1 text-xs text-charcoal-600">
                    <li><strong>Slået til (flueben):</strong> Sorten kan bestilles på "Bestil knolde" og vises samtidig i galleriet.</li>
                    <li><strong>Slået fra:</strong> Sorten skjules automatisk fra bestillingslisten/salg, men <strong>bliver altid stående i galleriet</strong>, så jeres gæster og kunder altid kan se blomsten og nyde billedet.</li>
                  </ul>
                </li>
                <li><strong>Opret en ny variant:</strong> Klik på <strong>"Opret ny"</strong> øverst til højre. Udfyld <code>navn</code>, <code>pris</code>, vælg bogstavgruppe, upload et billede og gem. Den bliver med det samme synlig i galleriet!</li>
              </ol>
            </div>
          </div>

          {/* Trin 3: Tekster og sider */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-dahlia-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                3
              </div>
              <h3 className="text-xl font-serif font-bold text-forest-950">Rediger tekster og billeder på siderne</h3>
            </div>
            <div className="pl-13 flex flex-col gap-2 text-charcoal-700 text-sm leading-relaxed">
              <p>Hvis du vil ændre tekster eller topbannere på f.eks. "Om os", "Stiklinger", "Bordpynt" eller "Forsiden":</p>
              <ol className="list-decimal pl-5 flex flex-col gap-2 mt-1">
                <li>Klik på samlingen <strong>"sider"</strong> i venstre sidepanel.</li>
                <li>Vælg den side, du ønsker at opdatere (f.eks. "Forside", "Om os" eller "Stiklinger").</li>
                <li>I feltet <code>indhold</code> kan du redigere brødteksten direkte med overskrifter, lister og fed skrift.</li>
                <li>Under <code>banner_billede</code> kan du udskifte det store billede øverst på siden.</li>
                <li>Klik på <strong>"Gem"</strong> øverst til højre. Ændringerne vises på hjemmesiden med det samme!</li>
              </ol>
            </div>
          </div>

          {/* Trin 4: Billeder i galleriet */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-dahlia-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                4
              </div>
              <h3 className="text-xl font-serif font-bold text-forest-950">Galleriet og ekstra billeder</h3>
            </div>
            <div className="pl-13 flex flex-col gap-2 text-charcoal-700 text-sm leading-relaxed">
              <p>Galleriet på hjemmesiden samler automatisk alle billeder og præsenterer dem alfabetisk i en flot fotovisning:</p>
              <ul className="list-disc pl-5 flex flex-col gap-2 mb-2">
                <li><strong>Alle dahlia-varianter (ca. 175 stk.):</strong> Hentes automatisk direkte fra samlingen <code>dahliaknolde</code>. Uanset om en knold aktuelt er sat til salg eller ej, bliver den altid vist i galleriet, så I slipper for at oprette de samme blomster to steder!</li>
                <li><strong>Ekstra billeder og stemningsfotos (samlingen <code>galleri</code>):</strong> Hvis I vil vise billeder af pluk-selv marken, gården, arrangementer eller blomsterdekorationer, tilføjes de under samlingen <strong>"galleri"</strong>.</li>
              </ul>
              <ol className="list-decimal pl-5 flex flex-col gap-2 mt-1">
                <li>Klik på samlingen <strong>"galleri"</strong> i CMS.</li>
                <li>Klik på <strong>"Opret ny"</strong> for at tilføje et nyt foto (f.eks. havebillede eller dekoration).</li>
                <li>I feltet <code>titel</code> skriver du motivets navn.</li>
                <li>Under <code>billede</code> uploader du fotoet.</li>
                <li>Klik på <strong>"Gem"</strong>. Billedet vises nu i galleriet sammen med alle jeres dahlia-blomster.</li>
              </ol>
            </div>
          </div>

          {/* Trin 5: Arrangementer */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-dahlia-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                5
              </div>
              <h3 className="text-xl font-serif font-bold text-forest-950">Billeder til Bordpynt &amp; Dekorationer</h3>
            </div>
            <div className="pl-13 flex flex-col gap-2 text-charcoal-700 text-sm leading-relaxed">
              <p>For at tilføje, fjerne eller ændre billeder specifikt på siden <strong>"Ideer til bordpynt og dekorationer"</strong>:</p>
              <ol className="list-decimal pl-5 flex flex-col gap-2 mt-1">
                <li>Klik på samlingen <strong>"Bordpynt &amp; Dekorationer Galleri"</strong> (<code>bordpynt_galleri</code>) i venstre panel.</li>
                <li>Klik på <strong>"Opret ny"</strong> for at uploade et nyt billede med bordpynt, buketter eller dekorationer.</li>
                <li>Udfyld <code>titel</code> (overskrift) og upload billedet under <code>billede</code>.</li>
                <li>(Valgfrit) Angiv et tal under <code>sortering</code> for at styre rækkefølgen af billederne (f.eks. 1, 2, 3...).</li>
                <li>Klik på <strong>"Gem"</strong>. Billederne vises nu udelukkende i inspirationsgalleriet på siden for bordpynt!</li>
              </ol>
            </div>
          </div>

          {/* Trin 6: Arrangementer */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-dahlia-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                6
              </div>
              <h3 className="text-xl font-serif font-bold text-forest-950">Opret og rediger arrangementer</h3>
            </div>
            <div className="pl-13 flex flex-col gap-2 text-charcoal-700 text-sm leading-relaxed">
              <p>For at tilføje nye arrangementer eller åbent hus på gården:</p>
              <ol className="list-decimal pl-5 flex flex-col gap-2 mt-1">
                <li>Klik på samlingen <strong>"arrangementer"</strong> i venstre sidepanel.</li>
                <li>Udfyld <code>titel</code> (overskrift), <code>dato_tekst</code> (f.eks. "Dato: 19. juli 2026") og <code>beskrivelse</code>.</li>
                <li>Vælg arrangementets <code>status</code> via dropdown-menuen (f.eks. "Kommende", "Afholdt" eller "Aflyst").</li>
                <li>Klik på <strong>"Gem"</strong>. Arrangementet optræder nu automatisk både på forsiden og undersiden for arrangementer.</li>
              </ol>
            </div>
          </div>

          {/* Trin 7: Anmeldelser, Slides & Kontaktinfo */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-dahlia-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                7
              </div>
              <h3 className="text-xl font-serif font-bold text-forest-950">Anmeldelser, Forside-slides &amp; Kontaktinfo</h3>
            </div>
            <div className="pl-13 flex flex-col gap-2 text-charcoal-700 text-sm leading-relaxed">
              <ul className="list-disc pl-5 flex flex-col gap-2">
                <li><strong>Anmeldelser ("anmeldelser"):</strong> Tilføj eller ret kundeanbefalinger med navn, by, antal stjerner (1-5) og udtalelse.</li>
                <li><strong>Forside-slides ("forside_slides"):</strong> Skift billeder, overskrifter og knapper i den store billedslider på forsiden.</li>
                <li><strong>Kontaktinformation ("kontaktinformation"):</strong> Opdater telefonnummer, e-mail, adresse, CVR eller åbningstider ét centralt sted – ændringen slår igennem overalt på siden og i footeren.</li>
              </ul>
            </div>
          </div>

          {/* Support */}
          <div className="mt-6 bg-forest-50 p-6 rounded-2xl border border-forest-100 flex items-start gap-4">
            <i className="fa-solid fa-circle-info text-2xl text-forest-700 mt-0.5" />
            <div className="text-sm text-forest-900 leading-relaxed">
              <h4 className="font-bold mb-1">Har du brug for hjælp?</h4>
              <p>Hvis du oplever problemer eller har spørgsmål til andre dele af hjemmesiden, er du altid velkommen til at kontakte supporten. Vi står altid klar til at hjælpe dig!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
